import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import { f as fetchHTML } from './fetchHTML-9PhJW9Dj.js';
import { s as setLocalization } from './setLocalization-7tixTteg.js';
import './notyf-DIw0RvBj.js';

// Queried from the current market's root, as Shopify documents — not the domain
// root, which would ask about a different browsing context than the page.
const ENDPOINT = 'browsing_context_suggestions.json?country[enabled]=true';
const STRINGS_SECTION = 'fetch-localization-strings';
const STORAGE_KEY = 'kl:region-suggestion-dismissed';
const COUNTRY_TOKEN = '%country%';
const DISMISS_DAYS = 30;

class LocalizationSuggestion extends HTMLElement {
  async connectedCallback() {
    if (this.isDismissed()) return;

    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    // Deliberately not cached across page loads: this must re-check on every
    // visit (including a plain reload) so it keeps prompting until the visitor
    // makes an actual choice, rather than going stale on a stored answer.
    const url = await this.resolveMarketUrl();
    if (!url) return;

    // Copy for the market we're sending them to, not the one they're on. Without
    // it there is nothing worth showing — a prompt in the wrong language is the
    // very thing this dialog exists to avoid — so bail rather than fall back.
    const strings = await this.fetchStrings(url, this.detectedCountry);
    if (!strings) return;

    this.populate(url, strings);
    this.bindActions();
    Pubsub.publish('dialog:open', { dialogId: this.dialog.id });
  }

  /**
   * Localized homepage of the visitor's detected market, or null when there is
   * nothing to suggest (undetected, already here, or no homepage known for it).
   * @return {Promise<string|null>}
   */
  async resolveMarketUrl() {
    const detected = await this.detectCountry();
    if (!detected || detected === this.dataset.currentCountry) return null;

    this.detectedCountry = detected;

    // Only worth parsing once we know there is a market to look up.
    return this.parseMap()?.[detected] || null;
  }

  /**
   * @return {Object|null}
   */
  parseMap() {
    const script = this.querySelector('[data-localization-map]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Localization map is not valid JSON', error);
      return null;
    }
  }

  /**
   * Ask Shopify's geolocation endpoint for the visitor's country.
   * @return {Promise<string|null>} Lowercased ISO country code, e.g. "nl"
   */
  async detectCountry() {
    try {
      const url = `${Shopify.routes.root}${ENDPOINT}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.detected_values?.country?.handle?.toLowerCase() || null;
    } catch (error) {
      console.error('Could not detect visitor country', error);
      return null;
    }
  }

  /**
   * Load the dialog's copy as rendered by the target market itself. Liquid's `t`
   * filter only ever resolves against the current request's locale, so asking
   * that market's own URL for it is the only way to get its wording.
   *
   * That market cannot name the detected country server-side — `localization.country`
   * there still resolves to the market being browsed — so it ships every country's
   * name in its own language and the detected one is picked out here.
   * @param {string} marketUrl - Root-relative URL of the suggested market.
   * @param {string} detectedCountry - Lowercased ISO country code, e.g. "nl".
   * @return {Promise<{locale: string, country: string, message: string}|null>}
   */
  async fetchStrings(marketUrl, detectedCountry) {
    try {
      const html = await fetchHTML(`${marketUrl}?section_id=${STRINGS_SECTION}`);
      const root = html.querySelector('[data-localization-strings]');
      if (!root) return null;

      const country = root.querySelector(`[data-country-iso="${detectedCountry}"]`)?.dataset
        .countryName;
      if (!country) return null;

      return {
        locale: root.dataset.locale,
        country,
        message: root.dataset.message.replace(COUNTRY_TOKEN, country),
      };
    } catch (error) {
      console.error('Could not load suggestion copy for the target market', error);
      return null;
    }
  }

  /**
   * @param {string} url - Localized homepage of the suggested market.
   * @param {Object} strings - That market's own copy.
   */
  populate(url, strings) {
    // The dialog now speaks a different language than the page around it.
    this.dialog.lang = strings.locale;

    const message = this.querySelector('[data-message]');
    if (message) message.textContent = strings.message;

    this.confirm = this.querySelector('[data-confirm]');
    if (!this.confirm) return;

    // The `button` snippet always renders a label, so this is never missing.
    this.confirm.querySelector('.button__label').textContent = strings.country;
    this.confirm.href = url;
  }

  bindActions() {
    // Only an explicit choice — switching or deliberately staying — is
    // remembered. Closing the dialog any other way (the header's close button,
    // clicking outside, Escape) is not a choice, so it prompts again next visit.
    this.confirm?.addEventListener('click', (event) => this.handleConfirm(event));

    this.querySelector('[data-stay]')?.addEventListener('click', () => {
      this.remember();
      Pubsub.publish('dialog:close', { dialogId: this.dialog.id });
    });
  }

  /**
   * Record the switch with Shopify rather than only landing on the market's URL,
   * so it isn't undone by a geo-redirect on the next visit to the root. The
   * anchor's href stays the no-JS path.
   * @param {MouseEvent} event
   */
  handleConfirm(event) {
    this.remember();

    event.preventDefault();
    setLocalization(
      this.detectedCountry.toUpperCase(),
      this.dialog.lang,
      this.confirm.getAttribute('href')
    );
  }

  remember() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Ignore storage failures (private mode) — modal just reappears next load.
    }
  }

  /**
   * @return {boolean}
   */
  isDismissed() {
    try {
      const stored = Number(localStorage.getItem(STORAGE_KEY));
      if (!stored) return false;
      const ageDays = (Date.now() - stored) / (1000 * 60 * 60 * 24);
      return ageDays < DISMISS_DAYS;
    } catch {
      return false;
    }
  }
}

customElements.define('localization-suggestion', LocalizationSuggestion);
